import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import type { Toast, ToastInput, ToastContextValue } from '@shared/types';

const MAX_TOASTS = 3;
const DEFAULT_DURATION = 5000;

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (input: ToastInput): string => {
      const id = generateId();
      const toast: Toast = {
        ...input,
        id,
        duration: input.duration ?? DEFAULT_DURATION,
        createdAt: Date.now(),
      };

      setToasts((prev) => {
        // If at max capacity, remove the oldest toast
        const updated = prev.length >= MAX_TOASTS ? prev.slice(1) : prev;
        return [...updated, toast];
      });

      return id;
    },
    [generateId]
  );

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Auto-dismiss toasts after their duration
  useEffect(() => {
    if (toasts.length === 0) return;

    const timers = toasts.map((toast) => {
      const remaining = toast.duration! - (Date.now() - toast.createdAt);
      if (remaining <= 0) {
        removeToast(toast.id);
        return null;
      }
      return setTimeout(() => removeToast(toast.id), remaining);
    });

    return () => {
      timers.forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [toasts, removeToast]);

  const value: ToastContextValue = {
    toasts,
    maxToasts: MAX_TOASTS,
    addToast,
    removeToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
